import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle2, Instagram, MessageCircle, Radio } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { categories, products } from '../data/products'

export function Hero() {
  const { t } = useTranslation()

  return (
    <section className="relative h-screen w-full overflow-hidden">
      <div className="absolute inset-0">
        <video autoPlay loop muted playsInline className="h-full w-full scale-110 object-cover" aria-label="Saatbahane luxury watch collection">
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/60" />
      </div>
      <div className="absolute left-6 top-6 z-20">
        <img src="/logo.png" alt="Saatbahane" className="h-16 opacity-90 md:h-20" />
      </div>
      <div className="relative z-10 flex h-full flex-col items-center justify-center px-4 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-4 text-5xl font-bold tracking-tighter text-white md:text-7xl"
        >
          {t('hero.title')}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-8 text-xl font-light text-gray-200 md:text-2xl"
        >
          {t('hero.subtitle')}
        </motion.p>
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          href="https://wa.me/905305000764"
          target="_blank"
          rel="noopener noreferrer"
          className="cursor-pointer rounded-full bg-gold-500 px-8 py-3 font-semibold text-black shadow-lg transition-colors duration-300 hover:bg-gold-600 hover:shadow-gold-500/20"
        >
          {t('cta.whatsapp')}
        </motion.a>
      </div>
    </section>
  )
}

export function LiveBadge() {
  return (
    <motion.a
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, delay: 1 }}
      href="https://instagram.com/saatbahane"
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-8 left-8 z-40 hidden cursor-pointer transition-transform hover:scale-105 md:flex"
    >
      <div className="flex max-w-sm items-center space-x-4 rounded-xl border border-red-500/30 bg-gradient-to-r from-red-600 to-red-800 p-4 text-white shadow-lg">
        <div className="relative">
          <Radio className="h-8 w-8 animate-pulse" aria-hidden="true" />
          <span className="absolute -right-1 -top-1 h-3 w-3 animate-ping rounded-full bg-white" />
        </div>
        <div>
          <h2 className="text-sm font-bold uppercase tracking-wider">Canlı Yayın</h2>
          <p className="text-xs text-red-100">Her Pazartesi & Perşembe 20:30&apos;da</p>
        </div>
      </div>
    </motion.a>
  )
}

function ProductCard({ product }) {
  const { t, i18n } = useTranslation()
  const { image, title, brand, id, description } = product
  const language = i18n.resolvedLanguage?.split('-')[0] || 'tr'
  const productDescription = description?.[language] || description?.tr || t('collection.desc_placeholder')
  const message = `Merhaba, ${brand} ${title} hakkında bilgi almak istiyorum. (Ürün ID: ${id})`
  const inquiryUrl = `https://wa.me/905305000764?text=${encodeURIComponent(message)}`

  return (
    <motion.article
      layout
      whileHover={{ y: -10 }}
      className="group relative overflow-hidden rounded-xl border border-white/5 bg-dark-800 shadow-xl transition-all duration-500 hover:border-gold-500/50"
    >
      <div className="relative h-80 overflow-hidden">
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 transition-opacity group-hover:opacity-40" />
        <img src={image} alt={`${brand} ${title}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
        <div className="absolute right-4 top-4 z-20">
          <span className="rounded-full bg-gold-500/90 px-3 py-1 text-xs font-bold uppercase tracking-wider text-black backdrop-blur-sm">{t('collection.original')}</span>
        </div>
      </div>
      <div className="relative z-20 p-6">
        <p className="mb-1 text-sm font-semibold uppercase tracking-widest text-gold-500">{brand}</p>
        <h3 className="mb-2 text-2xl font-bold text-white">{title}</h3>
        <p className="mb-6 line-clamp-2 text-sm text-gray-400">{productDescription}</p>
        <div className="flex items-center justify-between">
          <span className="font-mono text-lg text-white">{t('collection.ask_price')}</span>
          <a
            href={inquiryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-white transition-all duration-300 hover:bg-gold-500 hover:text-black group-hover:shadow-[0_0_20px_rgba(234,179,8,0.3)]"
          >
            <MessageCircle size={18} aria-hidden="true" />
            <span className="text-sm font-medium">{t('collection.inquire')}</span>
          </a>
        </div>
      </div>
    </motion.article>
  )
}

export function Collection() {
  const { t } = useTranslation()
  const [selectedCategory, setSelectedCategory] = useState('All')
  const visibleProducts = selectedCategory === 'All' ? products : products.filter(({ category }) => category === selectedCategory)

  return (
    <section className="relative bg-dark-900 py-20" id="collection">
      <div className="pointer-events-none absolute left-0 top-0 h-full w-full overflow-hidden">
        <div className="absolute left-10 top-20 h-64 w-64 rounded-full bg-gold-500/5 blur-3xl" />
        <div className="absolute bottom-20 right-10 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl" />
      </div>
      <div className="container relative z-10 mx-auto px-4">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-4xl font-bold text-white md:text-5xl"><span className="text-gold-500">{t('collection.title_highlight')}</span> {t('collection.title')}</h2>
          <p className="mx-auto mb-8 max-w-2xl text-gray-400">{t('collection.subtitle')}</p>
          <div className="mb-12 flex flex-wrap justify-center gap-4" aria-label="Watch categories">
            {categories.map((category) => (
              <button
                key={category}
                type="button"
                onClick={() => setSelectedCategory(category)}
                className={`rounded-full border px-6 py-2 text-sm font-semibold transition-all duration-300 ${selectedCategory === category ? 'border-gold-500 bg-gold-500 text-black shadow-[0_0_15px_rgba(234,179,8,0.4)]' : 'border-white/10 bg-transparent text-gray-400 hover:border-gold-500/50 hover:text-white'}`}
              >
                {category === 'All' ? t('collection.all') : category}
              </button>
            ))}
          </div>
        </div>
        <motion.div layout className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence>
            {visibleProducts.map((product) => <ProductCard key={product.id} product={product} />)}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}

export function SellWatch() {
  const { t } = useTranslation()
  const reasons = [t('sell_watch.reason1'), t('sell_watch.reason2'), t('sell_watch.reason3')]

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-dark-800 to-dark-900 py-20">
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-gold-500/5 blur-[100px]" />
      <div className="container relative z-10 mx-auto px-4">
        <div className="flex flex-col items-center justify-between gap-12 md:flex-row">
          <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="md:w-1/2">
            <h2 className="mb-6 text-4xl font-bold leading-tight md:text-5xl"><span className="bg-gradient-to-r from-gold-400 to-gold-600 bg-clip-text text-transparent">{t('sell_watch.title')}</span></h2>
            <p className="mb-8 rounded-r-lg border-l-4 border-gold-500 bg-white/5 py-2 pl-4 text-xl font-light italic text-gray-300">&quot;{t('sell_watch.subtitle')}&quot;</p>
            <p className="mb-8 text-lg leading-relaxed text-gray-400">{t('sell_watch.desc')}</p>
            <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {reasons.map((reason) => (
                <div key={reason} className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-gold-500" aria-hidden="true" />
                  <span>{reason}</span>
                </div>
              ))}
            </div>
            <motion.a
              href="https://wa.me/905077416327"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 rounded-full bg-[#25D366] px-8 py-4 text-lg font-bold text-white shadow-[0_4px_15px_rgba(37,211,102,0.4)] transition-all hover:shadow-[0_6px_20px_rgba(37,211,102,0.6)]"
            >
              <MessageCircle className="h-6 w-6" aria-hidden="true" />
              {t('sell_watch.cta')}
            </motion.a>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative md:w-1/2">
            <div className="relative z-10 rounded-2xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
              <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-gray-800 to-black">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1547996160-81dfa63595aa?q=80&w=2154&auto=format&fit=crop')] bg-cover bg-center opacity-70 transition-transform duration-700 hover:scale-105" />
                <div className="absolute inset-0 bg-black/40" />
                <div className="z-10 m-8 rounded-xl border border-gold-500/30 bg-black/60 p-6 text-center backdrop-blur-sm">
                  <div className="mb-2 text-4xl text-gold-500">Rolex</div>
                  <div className="text-sm uppercase tracking-widest text-white/80">Expertise &amp; Buying</div>
                </div>
              </div>
            </div>
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gold-500/20 blur-2xl" />
            <div className="absolute -bottom-5 -left-5 h-24 w-24 rounded-full bg-blue-500/20 blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export function Social() {
  const { t } = useTranslation()
  return (
    <section className="relative overflow-hidden bg-black py-24">
      <div className="absolute inset-0 z-0 opacity-40">
        <video autoPlay loop muted playsInline className="h-full w-full object-cover" aria-label="Saatbahane Instagram auctions">
          <source src="/social.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black" />
      </div>
      <div className="container relative z-10 mx-auto px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl">
          <div className="mb-6 inline-flex animate-pulse items-center space-x-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-1.5 text-sm font-bold text-white">
            <Radio size={14} fill="currentColor" aria-hidden="true" /><span>CANLI / LIVE</span>
          </div>
          <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">{t('social.title')}</h2>
          <p className="mb-8 text-xl font-light text-gray-300">{t('social.subtitle')}</p>
          <a href="https://www.instagram.com/saatbahane/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 rounded-full bg-white px-8 py-4 text-lg font-bold text-black transition-colors hover:bg-gold-500">
            <Instagram size={24} aria-hidden="true" /><span>@saatbahane</span>
          </a>
        </motion.div>
      </div>
    </section>
  )
}

